import {
  CSSProperties,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';
import { Charger } from '../../../stores/types';
import { ColorType, Label, LabelType, Spinner } from '../../_ui';
import { defaultColorPalettes, isHexColor } from '../../_ui/shared/Colors.g';
import './NearbyChargerFinder.css';
import { NearbyChargerAddressField } from './NearbyChargerAddressField.component';
import { NearbyChargerFilters } from './NearbyChargerFilters.component';
import { NearbyChargerResults } from './NearbyChargerResults.component';
import { FinderFilters, FinderOrigin } from './types';
import { useNearbyChargerSearch } from './useNearbyChargerSearch';
import { getChargerDefaultFilters, getChargerOrigin } from './utils/chargerDefaults';
import { isSameLocation } from './utils/geo';
import { filterByNetworks, getNetworkOptions } from './utils/networks';
import { RESULT_LIMIT } from './utils/search';

// The map library is large and only support users open the finder, so load it on demand
const NearbyChargerMap = lazy(async () => {
  const { NearbyChargerMap: Map } = await import('./NearbyChargerMap.component');
  return { default: Map };
});

const MapLoading = () => (
  <div className='w-full h-full grid place-items-center bg-grey1'>
    <Spinner height='h-8' width='w-8' />
  </div>
);

interface NearbyChargerFinderProps {
  charger: Charger;
}

export const NearbyChargerFinder = ({ charger }: NearbyChargerFinderProps) => {
  const { t } = useTranslation();
  const theme = useSelector(getCurrentTheme);
  const { state: searchState, search, showError } = useNearbyChargerSearch();

  const chargerOrigin = useMemo(() => getChargerOrigin(charger), [charger]);
  const [origin, setOrigin] = useState<FinderOrigin | null>(chargerOrigin);
  const [unresolvedAddress, setUnresolvedAddress] = useState<string | null>(null);
  const [filters, setFilters] = useState<FinderFilters>(() => getChargerDefaultFilters(charger));
  const [activeId, setActiveId] = useState<number | null>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const palettePrimary = theme.colorPalettes?.primary;
  const primaryColor =
    palettePrimary && isHexColor(palettePrimary)
      ? palettePrimary
      : defaultColorPalettes.denim.primary;

  const candidates = useMemo(
    () => (searchState.phase === 'done' ? searchState.candidates : []),
    [searchState],
  );
  const networkOptions = useMemo(() => getNetworkOptions(candidates), [candidates]);
  // A network picked earlier may not be near this search; ignore it, as the dropdown does
  const selectedNetworkIds = useMemo(
    () => filters.networkIds.filter((id) => networkOptions.some((option) => option.id === id)),
    [filters.networkIds, networkOptions],
  );
  // The network filter applies instantly to the current results; other filters need a new search
  const results = useMemo(
    () => filterByNetworks(candidates, selectedNetworkIds).slice(0, RESULT_LIMIT),
    [candidates, selectedNetworkIds],
  );

  const runSearch = useCallback(
    (searchOrigin: FinderOrigin, searchFilters: FinderFilters) => {
      setActiveId(null);
      search(searchOrigin, searchFilters);
    },
    [search],
  );

  // Every field is pre-filled from the charger, so search as soon as the finder opens
  useEffect(() => {
    if (chargerOrigin) runSearch(chargerOrigin, filters);
  }, []);

  // Keep the selected card visible when it is picked on the map
  useEffect(() => {
    if (activeId !== null) {
      cardRefs.current.get(activeId)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeId]);

  const handleFiltersChange = useCallback((changes: Partial<FinderFilters>) => {
    setFilters((current) => ({ ...current, ...changes }));
    // The selected result may no longer be listed
    if (changes.networkIds) setActiveId(null);
  }, []);

  const handleSearch = () => {
    if (unresolvedAddress !== null || !origin) {
      showError(
        unresolvedAddress?.trim()
          ? 'nearby_chargers_pick_address'
          : 'nearby_chargers_enter_address',
      );
      return;
    }
    runSearch(origin, filters);
  };

  const handleOriginSelect = (nextOrigin: FinderOrigin | null) => {
    if (!nextOrigin) {
      setUnresolvedAddress('');
      return;
    }
    setOrigin(nextOrigin);
    setUnresolvedAddress(null);
    runSearch(nextOrigin, filters);
  };

  const handleResetToCharger = () => {
    if (!chargerOrigin) return;
    setOrigin(chargerOrigin);
    setUnresolvedAddress(null);
    runSearch(chargerOrigin, filters);
  };

  const setCardRef = useCallback((poiId: number, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(poiId, element);
    } else {
      cardRefs.current.delete(poiId);
    }
  }, []);

  const mapOrigin = searchState.phase === 'done' ? searchState.origin : origin;
  const isMapOriginCharger = isSameLocation(mapOrigin, chargerOrigin);

  return (
    <div
      className='nearby-charger-finder flex flex-row gap-4 h-[720px] max-h-[calc(100vh-160px)] min-h-[520px]'
      style={{ '--finder-primary': primaryColor } as CSSProperties}
      data-testid='nearby-charger-finder'
    >
      <div className='flex flex-col gap-3 w-[400px] shrink-0 min-h-0'>
        <NearbyChargerAddressField
          chargerOrigin={chargerOrigin}
          canResetToCharger={
            !!chargerOrigin &&
            (!isSameLocation(origin, chargerOrigin) || unresolvedAddress !== null)
          }
          onOriginSelect={handleOriginSelect}
          onUnresolvedInput={setUnresolvedAddress}
          onResetToCharger={handleResetToCharger}
        />
        <NearbyChargerFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          networkOptions={networkOptions}
          isSearching={searchState.phase === 'loading'}
          onSearch={handleSearch}
        />
        <div className='border-t border-grey2' />
        <div className='flex-1 min-h-0 overflow-y-auto pr-1'>
          <NearbyChargerResults
            searchState={searchState}
            results={results}
            activeId={activeId}
            primaryColor={primaryColor}
            onSelect={setActiveId}
            cardRef={setCardRef}
          />
        </div>
        <Label
          text={t('nearby_chargers_data_disclaimer')}
          type={LabelType.LABEL_XXS_MEDIUM}
          color={ColorType.GREY5}
        />
      </div>
      <div className='flex-1 min-w-0 rounded-lg overflow-hidden border border-grey2'>
        <Suspense fallback={<MapLoading />}>
          <NearbyChargerMap
            origin={mapOrigin}
            originName={isMapOriginCharger ? charger.name ?? '' : mapOrigin?.label ?? ''}
            isOriginCharger={isMapOriginCharger}
            results={results}
            activeId={activeId}
            onSelect={setActiveId}
          />
        </Suspense>
      </div>
    </div>
  );
};
